<script lang="ts">
  // A draggable boundary between two side-by-side panes: a vertical
  // separator (WAI-ARIA window splitter) the parent positions with `class`.
  // Dragging reports the size through `oninput` once per frame, and the
  // final one through `onchange`; Escape ends a drag where it started
  // (`oninput(null)`). A `deferred` boundary, for a pane that is costly to
  // lay out, moves only a guide line while the pointer moves and reports a
  // size when the pointer rests (SETTLE_MS) or is released. The arrow keys
  // (Shift: five steps), Home and End report through `onchange` (an arrow
  // moves the boundary its way, so it shrinks a pane on the right); Enter
  // and a double click call `onreset`.
  import { onDestroy } from 'svelte';
  import { clamp, keyResize } from '../logic/resize';

  /** How long the pointer rests before a deferred drag sizes the pane. */
  const SETTLE_MS = 100;

  interface Props {
    /** The size shown now, in the units of min, max and step. */
    value: number;
    min: number;
    max: number;
    step: number;
    label: string;
    valuetext?: (v: number) => string;
    /** Units per pixel of pointer travel, read each frame of a drag (a layout may change it as the pane grows). */
    perPx?: () => number;
    /** 1 when dragging right grows the pane (it is left of the boundary), -1 when it shrinks it. */
    direction?: 1 | -1;
    /** Size the pane only when the pointer rests or is released, drawing a guide line meanwhile; a function is asked when each drag starts. */
    deferred?: boolean | (() => boolean);
    class?: string;
    /** IDs of the panes the separator sizes. */
    controls?: string;
    /** The size during a drag; null when the drag was cancelled, so the size from before it applies. */
    oninput?: (v: number | null) => void;
    onchange: (v: number) => void;
    onreset: () => void;
  }
  let { value, min, max, step, label, valuetext, perPx = () => 1, direction = 1, deferred = false, class: klass = '', controls, oninput, onchange, onreset }: Props = $props();

  /**
   * The drag: the pointer's x the size was last moved to, the size there
   * (unrounded), and the size the pane was last given (`applied`).
   */
  let drag: { x: number; value: number; applied: number; moved: boolean; deferred: boolean } | null = $state.raw(null);
  /** A deferred drag's guide line, in pixels from the boundary the pane has. */
  let guide: number | null = $state(null);
  let lastX = 0;
  let frame = 0;
  let settle: ReturnType<typeof setTimeout> | undefined;

  /** Move the dragged size by the pointer's travel to `x`, at the units per pixel of the layout now. */
  function follow(x: number): number {
    if (!drag) return value;
    drag.value = Math.max(min, Math.min(max, drag.value + (x - drag.x) * direction * perPx()));
    drag.x = x;
    return clamp(drag.value, { min, max });
  }

  function apply(v: number) {
    if (!drag) return;
    drag.applied = v;
    if (drag.deferred) guide = 0;
    oninput?.(v);
  }

  function tick() {
    frame = 0;
    if (!drag) return;
    const v = follow(lastX);
    if (!drag.deferred) return apply(v);
    guide = ((v - drag.applied) / perPx()) * direction;
    clearTimeout(settle);
    settle = setTimeout(() => drag && apply(clamp(drag.value, { min, max })), SETTLE_MS);
  }

  function onpointerdown(e: PointerEvent) {
    if (e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    (e.currentTarget as HTMLElement).focus({ preventScroll: true });
    const defer = typeof deferred === 'function' ? deferred() : deferred;
    drag = { x: e.clientX, value, applied: value, moved: false, deferred: defer };
    lastX = e.clientX;
    if (defer) guide = 0;
    document.documentElement.classList.add('pane-resizing');
  }

  function onpointermove(e: PointerEvent) {
    if (!drag) return;
    drag.moved ||= e.clientX !== lastX;
    lastX = e.clientX;
    if (!frame) frame = requestAnimationFrame(tick);
  }

  function end(e: PointerEvent) {
    if (!drag) return;
    const x = e.type === 'lostpointercapture' ? lastX : e.clientX;
    const moved = drag.moved || x !== lastX;
    const v = follow(x);
    stop();
    if (moved) onchange(v);
  }

  function stop() {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    clearTimeout(settle);
    drag = null;
    guide = null;
    document.documentElement.classList.remove('pane-resizing');
  }
  onDestroy(stop);

  function onkeydown(e: KeyboardEvent) {
    if (drag && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      stop();
      oninput?.(null);
      return;
    }
    const r = keyResize(e, value, { min, max }, step * direction);
    if (!r) return;
    e.preventDefault();
    e.stopPropagation();
    if (r.kind === 'reset') onreset();
    else if (r.value !== value) onchange(r.value);
  }
</script>

<!-- A focusable separator that takes pointer and keys is the WAI-ARIA window splitter. -->
<!-- svelte-ignore a11y_no_noninteractive_tabindex, a11y_no_noninteractive_element_interactions -->
<div
  class="resizer {klass}"
  class:dragging={drag}
  role="separator"
  tabindex="0"
  aria-orientation="vertical"
  aria-label={label}
  aria-controls={controls}
  aria-valuenow={value}
  aria-valuemin={min}
  aria-valuemax={max}
  aria-valuetext={valuetext?.(value)}
  title="{label} (drag, or arrow keys; double-click or Enter resets)"
  {onpointerdown}
  {onpointermove}
  onpointerup={end}
  onpointercancel={end}
  onlostpointercapture={end}
  ondblclick={onreset}
  {onkeydown}
>
  <span class="grip" aria-hidden="true"></span>
  {#if guide !== null}<span class="guide" aria-hidden="true" style:transform="translateX({Math.round(guide)}px)"></span>{/if}
</div>

<style>
  .resizer {
    position: absolute;
    z-index: 4;
    width: 12px;
    cursor: col-resize;
    touch-action: none;
    user-select: none;
    outline: none;
  }
  /* The boundary line, drawn while the handle is pointed at, focused or dragged. */
  .resizer::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 5px;
    width: 2px;
    border-radius: 2px;
    background: var(--accent);
    opacity: 0;
    transition: opacity 0.12s;
  }
  /* The grip stays in the middle of the window while a tall boundary scrolls past (a short one sets its own place). */
  /* A deferred drag's boundary-to-be. */
  .guide {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 5px;
    width: 2px;
    border-radius: 2px;
    background: var(--accent);
    box-shadow: 0 0 0 1px var(--surface);
    pointer-events: none;
  }
  .grip {
    position: sticky;
    top: calc(50vh - 15px);
    display: block;
    width: 10px;
    height: 30px;
    margin-left: 1px;
    border: 1px solid var(--line);
    border-radius: 5px;
    background:
      radial-gradient(circle, var(--muted) 1px, transparent 1.5px) center / 4px 6px repeat-y,
      var(--surface);
    opacity: 0;
    transition: opacity 0.12s;
  }
  .resizer:hover::before,
  .resizer.dragging::before {
    opacity: 0.55;
  }
  .resizer:focus-visible::before {
    opacity: 1;
  }
  .resizer:hover .grip,
  .resizer:focus-visible .grip,
  .resizer.dragging .grip {
    opacity: 1;
    border-color: var(--accent);
  }
  .resizer:focus-visible .grip {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  @media (prefers-reduced-motion: reduce) {
    .resizer::before,
    .grip {
      transition: none;
    }
  }
  /* While a boundary is dragged, the whole page shows the resize cursor and selects no text. */
  :global(.pane-resizing),
  :global(.pane-resizing *) {
    cursor: col-resize !important;
    user-select: none !important;
  }
  /* Phones stack the panes: nothing to resize. */
  @media (max-width: 760px) {
    .resizer {
      display: none;
    }
  }
</style>
